import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { activatePayPalSubscription, getPayPalConfig } from "@/lib/paypal.functions";
import { useAuth } from "@/hooks/use-auth";

type PlanId = "starter" | "pro" | "unlimited";

declare global {
  interface Window {
    paypal?: {
      Buttons: (opts: unknown) => { render: (sel: HTMLElement | string) => Promise<void> };
    };
  }
}

let sdkPromise: Promise<void> | null = null;
function loadPayPalSdk(clientId: string): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.paypal) return Promise.resolve();
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById("paypal-sdk") as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("PayPal SDK failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.id = "paypal-sdk";
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
      clientId,
    )}&vault=true&intent=subscription&components=buttons`;
    s.async = true;
    s.dataset.namespace = "paypal";
    s.onload = () => resolve();
    s.onerror = () => {
      sdkPromise = null;
      reject(new Error("PayPal SDK failed to load"));
    };
    document.head.appendChild(s);
  });
  return sdkPromise;
}

export function PayPalSubscribeButton({
  plan,
  onSuccess,
}: {
  plan: PlanId;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const activate = useServerFn(activatePayPalSubscription);
  const getConfig = useServerFn(getPayPalConfig);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cfg = await getConfig({});
        const planId = cfg.planIds[plan];
        if (!planId) throw new Error("PayPal plan not configured");
        await loadPayPalSdk(cfg.clientId);
        if (!mounted || !containerRef.current || !window.paypal) return;
        containerRef.current.innerHTML = "";
        await window.paypal
          .Buttons({
            style: { layout: "vertical", color: "gold", shape: "pill", label: "subscribe" },
            createSubscription: (_: unknown, actions: { subscription: { create: (o: unknown) => Promise<string> } }) => {
              if (!userId) throw new Error("You must be signed in to subscribe.");
              // custom_id proves ownership server-side when activating.
              return actions.subscription.create({ plan_id: planId, custom_id: userId });
            },
            onApprove: async (data: { subscriptionID: string }) => {
              try {
                await activate({ data: { plan, subscriptionId: data.subscriptionID } });
                toast.success(t("subscriptionCheckout.paymentSuccessful"));
                onSuccess?.();
              } catch (e) {
                toast.error(e instanceof Error ? e.message : "PayPal activation failed");
              }
            },
            onError: (err: unknown) => {
              console.error("paypal error", err);
              toast.error("PayPal error — please try again.");
            },
          })
          .render(containerRef.current);
        if (mounted) setLoading(false);
      } catch (e) {
        if (mounted) setError(e instanceof Error ? e.message : "Failed to load PayPal");
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  return (
    <div className="rounded-xl border border-border/60 bg-background/40 p-4">
      <div className="mb-3 text-sm text-muted-foreground">
        {t("subscriptionCheckout.paypalDescription", "You'll be redirected to PayPal to approve the subscription. It renews automatically each month and you can cancel anytime.")}
      </div>
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {t("subscriptionCheckout.preparingCheckout")}
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400">
          {error}
        </div>
      )}
      <div ref={containerRef} />
    </div>
  );
}
